import { useEffect } from 'react';

interface Subsection {
  subsection_number: string;
  title: string;
  content: string;
  prohibited_uses?: string;
  list_items?: string[];
  additional_content?: string;
}

interface Section {
  section_number: string;
  title: string;
  subsections?: Subsection[];
  content?: string;
  list_items?: string[];
  contact_info?: {
    support_team: string;
    email: string;
    mailing_address: {
      street: string;
      city: string;
      state: string;
      zip_code: string;
    };
  };
}

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const termsData = {
    "document_title": "Vital Hive Technologies",
    "document_subtitle": "Provider Terms of Service",
    "effective_date": "August 06, 2025",
    "preamble": "These Terms of Service (\"Terms\") constitute a binding agreement between you (\"Provider,\" \"you,\" or \"your\") and Vital Hive Technologies LLC and its affiliates (\"Vital Hive,\" \"VHT,\" \"Vital Hive Clinician App,\" \"we,\" \"us,\" or \"our\") regarding your access to and use of the Vital Hive website, VHT Clinician App, mobile application, and associated services (collectively, the \"Platform\").",
    "agreement_clause": "By registering for an account, accessing the Platform, or using any of the Services, you agree to be bound by these Terms and our Privacy Policy. If you do not agree, do not use the Platform.",
    "sections": [
      {
        "section_number": "1",
        "title": "ELIGIBILITY, ACCOUNT CREATION & MODIFICATIONS",
        "subsections": [
          {
            "subsection_number": "1.1",
            "title": "Eligibility",
            "content": "To access and use the Platform as a Provider, you must:",
            "list_items": [
              "Be at least 18 years of age;",
              "Possess a valid healthcare license or certification relevant to the services you intend to provide;",
              "Be legally authorized to work in the United States;",
              "Not be barred from accessing or using the Platform under applicable law;",
              "Maintain current professional liability insurance, if required."
            ]
          },
          {
            "subsection_number": "1.2",
            "title": "Account Setup",
            "content": "To use the Platform, you must create an account and provide accurate and complete information, including:",
            "list_items": [
              "Name, contact information, and credentials;",
              "Verification documents such as licensure, certifications, and insurance;",
              "Bank account or payment information for disbursements."
            ],
            "additional_content": "You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account."
          },
          {
            "subsection_number": "1.3",
            "title": "Modifications",
            "content": "Vital Hive may revise these Terms at any time by posting updated terms to the Platform. We will provide reasonable notice (email, in-app notice, etc.) of material changes. Your continued use of the Platform after changes take effect constitutes acceptance. Shifts already booked prior to changes will remain subject to the previous Terms, unless compliance with updated Terms is mandated by law."
          }
        ]
      },
      {
        "section_number": "2",
        "title": "USE OF THE PLATFORM",
        "subsections": [
          {
            "subsection_number": "2.1",
            "title": "Platform Access",
            "content": "Subject to compliance with these Terms, Vital Hive grants you a limited, non-exclusive, non-transferable license to access and use the Platform solely for the purpose of browsing and responding to shift opportunities posted by participating healthcare facilities.",
            "prohibited_uses": "You may not:",
            "list_items": [
              "Misrepresent your qualifications or identity;",
              "Use another Provider's account or allow anyone to access your account;",
              "Circumvent the Platform to engage with Facilities off-platform without consent;",
              "Use automated tools to extract data or interact with the Platform."
            ]
          },
          {
            "subsection_number": "2.2",
            "title": "Background & Credential Checks",
            "content": "By registering on the Platform, you authorize Vital Hive to:",
            "list_items": [
              "Verify your credentials and licensure with applicable boards or registries;",
              "Perform limited background checks;",
              "Share your verified credentials with Facilities when you request or accept shifts."
            ],
            "additional_content": "We reserve the right to suspend or revoke Platform access if information is found to be inaccurate, expired, or noncompliant."
          },
          {
            "subsection_number": "2.3",
            "title": "Code of Conduct",
            "content": "You agree to act professionally and ethically when using the Platform, including:",
            "list_items": [
              "Not engaging in any harassing, discriminatory, or unlawful behavior;",
              "Respecting patient privacy and HIPAA obligations;",
              "Following shift instructions provided by Facilities when engaged;",
              "Not soliciting Facilities for off-platform engagements in violation of these Terms."
            ]
          }
        ]
      },
      {
        "section_number": "3",
        "title": "NO EMPLOYMENT RELATIONSHIP",
        "subsections": [
          {
            "subsection_number": "3.1",
            "title": "Independent Contractor Status",
            "content": "You acknowledge that:",
            "list_items": [
              "Vital Hive is not your employer;",
              "Nothing in these Terms creates an employment, partnership, joint venture, or agency relationship;",
              "You are solely responsible for your tax obligations and benefits, including income tax, self-employment tax, and insurance."
            ],
            "additional_content": "You agree not to hold yourself out as an employee, representative, or agent of Vital Hive."
          }
        ]
      },
      {
        "section_number": "4",
        "title": "PAYMENTS & PLATFORM FEES",
        "subsections": [
          {
            "subsection_number": "4.1",
            "title": "Payment Facilitation",
            "content": "Vital Hive acts solely as a limited payment collection agent on your behalf for completed services and as the payment provider for your services through our third-party platform, Branch."
          },
          {
            "subsection_number": "4.2",
            "title": "Platform Fees",
            "content": "The Vital Hive Fee is a nominal deduction for each clinician shift worked. Per shift deductions include the Vital Hive Fee, $5.15 for 0-8 hours worked; $7.73 for 8-12 hours worked, and $10.30 for 12-16 hours worked. This fee is intended to cover the costs associated with the facilitation and exchange of services on the Vital Hive Platform, VHT Clinician. Additionally, a $1.25 per transaction will be deducted from all advances to include, same-day, next day, or any advance or disbursement. Vital Hive reserves the right to withhold payment if it is deemed by the client that the independent contractor failed to complete their assignments or left the position before the agreed-upon end date/time."
          }
        ]
      },
      {
        "section_number": "5",
        "title": "DATA PRIVACY & CONFIDENTIALITY",
        "subsections": [
          {
            "subsection_number": "5.1",
            "title": "Privacy",
            "content": "Your use of the Platform is subject to our Privacy Policy. You agree to maintain the confidentiality of any patient or facility information you encounter while fulfilling shifts. You may not use or disclose such information except as required for the performance of services and in accordance with applicable law."
          },
          {
            "subsection_number": "5.2",
            "title": "HIPAA Compliance",
            "content": "You are solely responsible for complying with HIPAA and all other data privacy obligations applicable to your professional services. You may be required to enter into a Business Associate Agreement with certain Facilities based on the nature of services provided."
          }
        ]
      },
      {
        "section_number": "6",
        "title": "TERMINATION",
        "subsections": [
          {
            "subsection_number": "6.1",
            "title": "Termination by You",
            "content": "You may terminate your account at any time by submitting written notice to support@vitalhivetechnologies.com. Termination will not affect your obligations under any previously accepted shifts or obligations under these Terms that survive termination."
          },
          {
            "subsection_number": "6.2",
            "title": "Suspension or Termination by Vital Hive",
            "content": "Vital Hive may suspend or terminate your access:",
            "list_items": [
              "For violation of these Terms;",
              "For noncompliance with licensure, credentialing, or conduct standards;",
              "Upon request by a regulatory authority or if required by law."
            ],
            "additional_content": "We may retain and archive shift-related data for regulatory or dispute resolution purposes even after account termination."
          }
        ]
      },
      {
        "section_number": "7",
        "title": "LIMITATIONS OF LIABILITY",
        "content": "To the fullest extent permitted by law:",
        "list_items": [
          "Vital Hive provides the Platform \"as-is\" and disclaims all warranties, express or implied;",
          "Vital Hive does not guarantee any minimum income, shift availability, or continued access;",
          "Vital Hive is not responsible for the conduct of any Facility or other Provider;",
          "In no event shall Vital Hive's total liability exceed the amount of Platform fees retained by Vital Hive from shifts completed by you in the six (6) months preceding the claim."
        ]
      },
      {
        "section_number": "8",
        "title": "INDEMNIFICATION",
        "content": "You agree to indemnify, defend, and hold harmless Vital Hive, its officers, directors, employees, and affiliates from any claims, liabilities, damages, or expenses (including legal fees) arising from:",
        "list_items": [
          "Your breach of these Terms;",
          "Your services performed during or outside of shifts;",
          "Your violation of law, including healthcare, tax, or licensing requirements."
        ]
      },
      {
        "section_number": "9",
        "title": "DISPUTE RESOLUTION",
        "content": "These Terms shall be governed by the laws of the State of Ohio. Any dispute shall be resolved in the state or federal courts located in Lucas County and you consent to their exclusive jurisdiction. You waive any right to participate in class or collective actions against Vital Hive."
      },
      {
        "section_number": "10",
        "title": "GENERAL TERMS",
        "list_items": [
          "Entire Agreement: These Terms, together with our Privacy Policy, represent the entire agreement between you and Vital Hive.",
          "Amendments: We may modify these Terms at any time by posting an updated version. Continued use of the Platform constitutes your acceptance of any such changes.",
          "Severability: If any provision is found unenforceable, the remainder shall remain in effect.",
          "No Waiver: Our failure to enforce any right does not waive it.",
          "Assignment: You may not assign these Terms without prior written consent. We may assign our rights and obligations freely."
        ]
      },
      {
        "section_number": "11",
        "title": "CONTACT",
        "content": "For questions or notices:",
        "contact_info": {
          "support_team": "Vital Hive Technologies Support Team",
          "email": "support@vitalhivetechnologies.com",
          "mailing_address": {
            "street": "3950 Sunforest Ct. Suite 200",
            "city": "Toledo",
            "state": "Ohio",
            "zip_code": "43623"
          }
        }
      }
    ]
  };

  const renderListItems = (items: string[]) => (
    <ul className="list-disc list-inside space-y-2 ml-6 text-gray-700">
      {items.map((item, index) => (
        <li key={index} className="text-base leading-relaxed">{item}</li>
      ))}
    </ul>
  );

  const renderSubsections = (subsections: Subsection[]) => (
    <div className="space-y-8">
      {subsections.map((subsection, index) => (
        <div key={index} className="border-l-4 border-blue-200 pl-6">
          <h4 className="text-xl font-semibold text-gray-800 mb-3">
            {subsection.subsection_number}. {subsection.title}
          </h4>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">{subsection.content}</p>
          {subsection.prohibited_uses && (
            <p className="text-gray-700 mb-3 font-medium text-base">{subsection.prohibited_uses}</p>
          )}
          {subsection.list_items && renderListItems(subsection.list_items)}
          {subsection.additional_content && (
            <p className="text-gray-700 mt-4 leading-relaxed text-base">{subsection.additional_content}</p>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-16">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Document Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              {termsData.document_title}
            </h1>
            <h2 className="text-xl font-semibold text-blue-600 mb-2">
              {termsData.document_subtitle}
            </h2>
            <p className="text-gray-600 text-base">
              Effective Date: {termsData.effective_date}
            </p>
          </div>
        </div>

        {/* Preamble */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">
              {termsData.preamble}
            </p>
            <p className="text-gray-700 leading-relaxed font-medium">
              {termsData.agreement_clause}
            </p>
          </div>
        </div>

        {/* Document Sections */}
        <div className="space-y-6">
          {termsData.sections.map((section: Section, index: number) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Section Header */}
              <div className="bg-gray-50 px-8 py-6 border-b border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <span className="text-3xl text-blue-600 font-bold">{section.section_number}.</span>
                  <span>{section.title}</span>
                </h3>
              </div>
              
              {/* Section Content */}
              <div className="p-8">
                {section.subsections ? (
                  renderSubsections(section.subsections)
                ) : (
                  <div className="prose prose-lg max-w-none">
                    {section.content && (
                      <p className="text-gray-700 mb-4 leading-relaxed text-base">{section.content}</p>
                    )}
                    {section.list_items && renderListItems(section.list_items)}
                    {section.contact_info && (
                      <div className="mt-6 p-6 bg-blue-50 rounded-lg border border-blue-200">
                        <h5 className="font-semibold text-gray-800 mb-3">{section.contact_info.support_team}</h5>
                        <p className="text-gray-700 mb-2">
                          Email: <span className="text-blue-600 font-medium">{section.contact_info.email}</span>
                        </p>
                        <p className="text-gray-700">
                          Address: {section.contact_info.mailing_address.street}, {section.contact_info.mailing_address.city}, {section.contact_info.mailing_address.state} {section.contact_info.mailing_address.zip_code}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Document Footer */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-8">
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              Last updated: {termsData.effective_date}
            </p>
            <p className="text-gray-500 text-xs mt-2">
              This document is legally binding and constitutes the complete agreement between parties.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
